import { defineStore } from 'pinia'
import { ref, reactive, computed } from 'vue'
import { useApi } from '@/utils/axios'
import { HORARIOS_FUNCIONAMENTO_ROTAS, REVENDEDOR_ROTAS } from '@/utils/apiRoutes'

export const useRevendedorStore = defineStore('revendedor', () => {
  // State
  const revendedores = ref([])
  const revendedor = ref(null)
  const loading = ref(false)
  const error = ref(null)

  // Pagination state
  const pagination = reactive({
    currentPage: 1,
    total: 0,
    perPage: 15,
    lastPage: 1,
  })

  // Filters state with default values
  const filtros = ref({
    razaosocial: '',
    responsavel: '',
    cnpj: '',
    ativo: '',
    status: '',
    data_inicial: '',
    data_final: '',
    cidade: '',
    estado: '',
  })

  // Track last requested page to prevent duplicate requests
  const lastRequestedPage = ref(null)

  // Simplified method that only fetches one page at a time - removed console.logs
  const buscarRevendedoresPage = async (page) => {
    // Prevent duplicate requests for the same page
    if (lastRequestedPage.value === page) {
      return revendedores.value
    }

    lastRequestedPage.value = page
    loading.value = true
    error.value = null

    try {
      // Create params object from filters
      const params = {
        ...filtros.value,
        page,
      }

      // Remove empty filters
      Object.keys(params).forEach((key) => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key]
        }
      })

      const response = await useApi(REVENDEDOR_ROTAS.LISTAR).fetch({ params })

      if (response?.data) {
        // Update data all at once to minimize reactivity triggers
        revendedores.value = response.data

        // Update pagination values
        if (response.meta) {
          pagination.currentPage = response.meta.current_page
          pagination.total = response.meta.total
          pagination.perPage = response.meta.per_page
          pagination.lastPage = response.meta.last_page
        }

        return response.data
      } else {
        throw new Error('Resposta inválida da API')
      }
    } catch (err) {
      error.value = err.message || 'Erro ao buscar revendedores'
      console.error('Erro ao buscar revendedores:', err)
      return []
    } finally {
      loading.value = false
    }
  }

  // Make buscarRevendedores simply redirect to the page method
  const buscarRevendedores = (page = 1) => {
    return buscarRevendedoresPage(page)
  }

  const updateHorariosFuncionamento = async (revendaId, horarios) => {
    try {
      loading.value = true
      error.value = null

      const response = await useApi(HORARIOS_FUNCIONAMENTO_ROTAS.SET_HORARIOS(revendaId)).post({
        ...horarios,
      })

      return {
        success: response.status === 'success',
        message: response.message || 'Horários de funcionamento atualizados com sucesso!',
      }
    } catch (error) {
      console.error('Erro ao atualizar horários de funcionamento:', error)
      error.value = error.response?.message || 'Erro ao atualizar horários de funcionamento.'

      return {
        success: false,
        message: error.response?.message || 'Erro ao atualizar horários de funcionamento.',
      }
    } finally {
      loading.value = false
    }
  }

  // Method to fetch a specific revendedor by ID | filters={revendas_per_page?:number, revendas_page?:number}
  const buscarRevendedorPorId = async (id, filters) => {
    loading.value = true
    error.value = null

    try {
      const response = await useApi(REVENDEDOR_ROTAS.REVENDEDOR(id)).fetch({ params: filters })

      if (response?.data) {
        revendedor.value = response.data
        return response.data
      } else {
        throw new Error('Revendedor não encontrado')
      }
    } catch (err) {
      error.value = err.message || 'Erro ao buscar revendedor'
      console.error('Erro ao buscar revendedor:', err)
      return null
    } finally {
      loading.value = false
    }
  }

  // Apply filters always goes back to page 1
  const aplicarFiltros = () => {
    lastRequestedPage.value = null // Reset last requested page
    return buscarRevendedoresPage(1)
  }

  // Method to reset all filters to default values
  const limparFiltros = () => {
    filtros.value = {
      razaosocial: '',
      responsavel: '',
      cnpj: '',
      ativo: 1,
      status: '',
      data_inicial: '',
      data_final: '',
      cidade: '',
      estado: '',
    }

    // Fetch with reset filters
    lastRequestedPage.value = null
    return buscarRevendedoresPage(1)
  }

  // Remove unused carregarMais method since we're using pagination now

  // Method to check if there's more data to load - keep for backwards compatibility
  const temMaisDados = computed(() => {
    return pagination.currentPage < pagination.lastPage
  })

  // Method to activate a revendedor
  const ativarRevendedor = async (revendedorId) => {
    if (!revendedorId) {
      return {
        success: false,
        message: 'ID do revendedor não informado',
      }
    }

    try {
      const response = await useApi(REVENDEDOR_ROTAS.ATIVAR_REVENDEDOR(revendedorId)).put()

      return {
        success: response.status === 'success',
        message: response.message || 'Revendedor ativado com sucesso!',
      }
    } catch (error) {
      console.error('Erro ao ativar revendedor:', error)
      return {
        success: false,
        message: error.response?.message || 'Erro ao ativar revendedor.',
      }
    }
  }

  // Method to deactivate a revendedor
  const desativarRevendedor = async (revendedorId, tempoRecarga) => {
    if (!revendedorId) {
      return {
        success: false,
        message: 'ID do revendedor não informado',
      }
    }

    try {
      const response = await useApi(REVENDEDOR_ROTAS.DESATIVAR_REVENDEDOR(revendedorId)).put({
        tempo_recarga: tempoRecarga,
      })

      return {
        success: response.status === 'success',
        message: 'Revendedor desativado com sucesso!',
      }
    } catch (error) {
      console.error('Erro ao desativar revendedor:', error)
      return {
        success: false,
        message: 'Erro ao desativar revendedor.',
      }
    }
  }

  // Method to update a revendedor
  const atualizarRevendedor = async (id, data) => {
    if (!id) {
      return {
        success: false,
        message: 'ID do revendedor não informado',
      }
    }

    loading.value = true
    error.value = null

    try {
      // Make PUT request to update revendedor
      const response = await useApi(REVENDEDOR_ROTAS.REVENDEDOR(id)).put(data)

      if (response.status === 'success') {
        // Update local revendedor data if available
        if (revendedor.value && revendedor.value.id === id) {
          Object.assign(revendedor.value, response.data || data)
        }

        return {
          success: true,
          message: response.message || 'Revendedor atualizado com sucesso!',
          data: response.data,
        }
      } else {
        throw new Error(response.message || 'Erro ao atualizar revendedor')
      }
    } catch (err) {
      error.value = err?.response?.data?.message || err.message || 'Erro ao atualizar revendedor'
      console.error('Erro ao atualizar revendedor:', err)

      return {
        success: false,
        message: err?.response?.data?.message || err.message || 'Erro ao atualizar revendedor',
      }
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    revendedores,
    revendedor,
    loading,
    error,
    pagination,
    filtros,
    temMaisDados, // This appears to be unused and could be removed

    // Methods
    buscarRevendedores,
    buscarRevendedoresPage,
    buscarRevendedorPorId,
    aplicarFiltros,
    limparFiltros,
    ativarRevendedor,
    desativarRevendedor,
    atualizarRevendedor,
    updateHorariosFuncionamento,
  }
})
